import React, { useState } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  AuthAbuser,
  AuthEventWindow,
  useGetAuthAbusersQuery,
  useGetAuthEventSummaryQuery,
} from '../../redux/adminApiSlice';

const WINDOW_LABEL: Record<AuthEventWindow, string> = {
  hour: 'Last hour',
  today: 'Today',
  day: 'Last 24h',
  week: 'Last 7 days',
  month: 'Last 30 days',
};

function fmtRelative(d: string | null | undefined): string {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** A small stat cell for the summary strip. */
function Stat({
  label,
  value,
  hint,
  emphasize,
}: {
  label: string;
  value: number | string;
  hint?: string;
  emphasize?: boolean;
}) {
  return (
    <Tooltip title={hint || ''} disableHoverListener={!hint}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1,
          bgcolor: emphasize ? 'rgba(224,36,96,0.10)' : 'action.hover',
          minWidth: 92,
        }}
      >
        <Typography variant="h6" sx={{ lineHeight: 1.1, color: emphasize ? '#E02460' : undefined }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * SMS + sign-in tracking. The top strip answers "how much am I spending on SMS
 * and how many sign-ins am I getting" across windows; the table below flags
 * phones hammering the code endpoint (the "one person signing in constantly →
 * probably ban them" view). Rows with a resolved account are clickable and
 * bubble the user up so the parent can open the shared UserModal.
 */
const SmsSignInPanel: React.FC<{
  onOpenUser?: (u: { _id: string; displayName?: string; username?: string }) => void;
}> = ({ onOpenUser }) => {
  const [summaryWindow, setSummaryWindow] = useState<AuthEventWindow>('today');
  const [abuserWindow, setAbuserWindow] = useState<AuthEventWindow>('day');

  const summary = useGetAuthEventSummaryQuery();
  const abusers = useGetAuthAbusersQuery({ window: abuserWindow, limit: 50, minCodes: 2 });

  const s = summary.data?.[summaryWindow];

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">SMS &amp; sign-ins</Typography>
        <Select
          size="small"
          value={summaryWindow}
          onChange={(e) => setSummaryWindow(e.target.value as AuthEventWindow)}
          sx={{ minWidth: 140 }}
        >
          {(Object.keys(WINDOW_LABEL) as AuthEventWindow[]).map((w) => (
            <MenuItem key={w} value={w}>
              {WINDOW_LABEL[w]}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {summary.isFetching && !s ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : s ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Stat
            label="paid SMS"
            value={s.smsSent}
            emphasize={s.smsSent > 0}
            hint="Real SMS actually sent (this costs money). Excludes dev/skipped sends."
          />
          <Stat
            label="codes sent"
            value={s.codesSent}
            hint="All verification codes generated (includes dev-skipped sends)."
          />
          <Stat label="verified" value={s.verified} hint="Codes entered correctly." />
          <Stat
            label="failed"
            value={s.failed}
            hint="Wrong / expired / too-many-attempts code entries."
          />
          <Stat label="logins" value={s.logins} hint="Existing users who signed in via phone." />
          <Stat label="new accounts" value={s.registrations} hint="Accounts created via phone." />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No auth events recorded yet.
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 3,
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="subtitle1">Heaviest code requesters</Typography>
          <Typography variant="caption" color="text.secondary">
            (≥2 codes; possible abuse / cost)
          </Typography>
        </Box>
        <Select
          size="small"
          value={abuserWindow}
          onChange={(e) => setAbuserWindow(e.target.value as AuthEventWindow)}
          sx={{ minWidth: 140 }}
        >
          {(Object.keys(WINDOW_LABEL) as AuthEventWindow[]).map((w) => (
            <MenuItem key={w} value={w}>
              {WINDOW_LABEL[w]}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {abusers.isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (abusers.data?.abusers.length ?? 0) === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No one requested multiple codes in this window. 🎉
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Phone</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Paid SMS</TableCell>
              <TableCell align="right">Codes</TableCell>
              <TableCell align="right">Verified</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell align="right">IPs</TableCell>
              <TableCell>Last</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(abusers.data?.abusers || []).map((a: AuthAbuser) => {
              const suspicious = a.codesSent >= 5 || a.distinctIps >= 3;
              const clickable = !!a.userId && !!onOpenUser;
              return (
                <TableRow
                  key={a.phone}
                  hover
                  onClick={
                    clickable
                      ? () =>
                          onOpenUser?.({
                            _id: a.userId as string,
                            displayName: a.displayName || undefined,
                            username: a.username || undefined,
                          })
                      : undefined
                  }
                  sx={{ cursor: clickable ? 'pointer' : 'default' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {a.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {a.userId ? (
                      <Tooltip title="View user details">
                        <Box>
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>
                            {a.displayName || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{a.username || '—'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Chip label="no account" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: a.smsSent > 0 ? 700 : 400, color: a.smsSent > 0 ? '#E02460' : undefined }}
                    >
                      {a.smsSent}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {suspicious ? (
                      <Chip label={a.codesSent} size="small" color="warning" />
                    ) : (
                      a.codesSent
                    )}
                  </TableCell>
                  <TableCell align="right">{a.verified}</TableCell>
                  <TableCell align="right">{a.failed}</TableCell>
                  <TableCell align="right">
                    {a.distinctIps >= 3 ? (
                      <Tooltip title="Many distinct IPs for one number — possible SMS pumping">
                        <Chip label={a.distinctIps} size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    ) : (
                      a.distinctIps
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fmtRelative(a.lastAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};

export default SmsSignInPanel;
